import React from 'react';
import { Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { F2110 } from '../../../services/pw2501Api';

const { Text } = Typography;

interface ApprovalTableProps {
  data: F2110[];
  loading: boolean;
  selectedKeys: string[];
  onSelectedChange: (keys: string[]) => void;
}

function formatNumber(value: number, decimals = 2): string {
  if (!value && value !== 0) return '--';
  return value.toLocaleString('zh-TW', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function renderStatus(stat: string): React.ReactNode {
  if (!stat || stat.trim() === '') return <Tag color="default">待核准</Tag>;
  if (stat === 'P') return <Tag color="green">已核准</Tag>;
  return <Tag color="blue">{stat}</Tag>;
}

const ApprovalTable: React.FC<ApprovalTableProps> = ({
  data,
  loading,
  selectedKeys,
  onSelectedChange,
}) => {
  const columns: ColumnsType<F2110> = [
    {
      title: '入庫單號',
      dataIndex: 'f2110_01',
      key: 'f2110_01',
      width: 110,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: '入庫日期',
      dataIndex: 'f2110_11',
      key: 'f2110_11',
      width: 110,
      render: (date: string | null) =>
        date ? new Date(date).toLocaleDateString('zh-TW') : '--',
    },
    {
      title: '廠商代碼',
      dataIndex: 'f2110_07',
      key: 'f2110_07',
      width: 90,
    },
    {
      title: '庫位代碼',
      dataIndex: 'f2110_12',
      key: 'f2110_12',
      width: 90,
    },
    {
      title: '供應商代碼',
      dataIndex: 'f2110_13',
      key: 'f2110_13',
      width: 110,
    },
    {
      title: '總金額',
      dataIndex: 'f2110_20',
      key: 'f2110_20',
      width: 130,
      align: 'right' as const,
      render: (v: number) => formatNumber(v),
    },
    {
      title: '核准狀態',
      dataIndex: 'f2110_80',
      key: 'f2110_80',
      width: 90,
      align: 'center' as const,
      render: renderStatus,
    },
    {
      title: <span data-contract-field="detail_count">明細筆數</span>,
      key: 'detail_count',
      width: 80,
      align: 'center' as const,
      render: (_: unknown, record: F2110) => record.details?.length || 0,
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys: React.Key[]) => onSelectedChange(keys as string[]),
  };

  return (
    <Table<F2110>
      rowKey="f2110_01"
      columns={columns}
      dataSource={data}
      loading={loading}
      rowSelection={rowSelection}
      size="small"
      pagination={{
        pageSize: 50,
        showSizeChanger: false,
        showTotal: (t: number) => `${t} 筆`,
      }}
      scroll={{ x: 800 }}
      locale={{ emptyText: '無待核准資料' }}
    />
  );
};

export default ApprovalTable;
